import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Save } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import AiPromptConfigService from '../../services/aiPromptConfigService';
import AiRfpService from '../../services/aiRfpService';

const INITIAL_FORM = {
  projectName: '',
  projectType: '',
  department: '',
  location: '',
  scope: '',
  budget: '',
  timeline: '',
  templateSelection: '',
};

const AiRfpInput = () => {
  const navigate = useNavigate();
  const { error: showError, success, toasts, removeToast } = useToast();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [activePrompt, setActivePrompt] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftId, setDraftId] = useState(null);

  useEffect(() => {
    const loadActivePrompt = async () => {
      try {
        const prompt = await AiPromptConfigService.getActivePrompt();
        setActivePrompt(prompt || null);
      } catch {
        setActivePrompt(null);
      }
    };

    loadActivePrompt();
  }, []);

  const templateOptions = useMemo(
    () => [
      'General Procurement',
      'IT Services',
      'Construction Works',
      'Facility Management',
      'Professional Services',
    ],
    [],
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const response = await AiRfpService.createDraft(formData, activePrompt);
      const createdDraftId = response?.data?._id;
      if (createdDraftId) {
        setDraftId(createdDraftId);
      }
      success('AI RFP draft saved successfully.');
    } catch (err) {
      showError(
        err?.message ||
          err?.response?.data?.message ||
          'Failed to save AI RFP draft.',
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      const response = await AiRfpService.generateDraft({
        draftId,
        inputPayload: formData,
        promptConfig: activePrompt,
      });

      const generatedContent = response?.data?.generatedContent;
      const generatedDraftId = response?.data?.draftId;

      if (!generatedContent) {
        throw new Error('Generated AI content is empty.');
      }

      if (generatedDraftId) {
        setDraftId(generatedDraftId);
      }

      navigate('/sourcing/rfps/create/workspace', {
        state: {
          generatedContent,
          inputPayload: formData,
          promptConfigInjection: activePrompt || null,
          draftId: generatedDraftId || draftId || null,
        },
      });
    } catch (err) {
      showError(
        err?.message ||
          err?.response?.data?.message ||
          'Failed to generate AI RFP draft.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const hasAnyInput = Object.values(formData).some(
    (value) => String(value).trim().length > 0,
  );

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="AI RFP Input"
        description="Capture structured project inputs for AI-assisted RFP draft generation."
      />

      <div className="page-container">

        <div
          className="card"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '20px' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Sparkles size={16} />
              <h6 className="mb-0">Form Card</h6>
            </div>

            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label">Project Name</label>
                <input
                  className="form-control"
                  value={formData.projectName}
                  onChange={(e) => handleChange('projectName', e.target.value)}
                  placeholder="Enter project name"
                />
              </div>

              <div className="col-lg-3">
                <label className="form-label">Project Type</label>
                <input
                  className="form-control"
                  value={formData.projectType}
                  onChange={(e) => handleChange('projectType', e.target.value)}
                  placeholder="Type"
                />
              </div>

              <div className="col-lg-3">
                <label className="form-label">Department</label>
                <input
                  className="form-control"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="Department"
                />
              </div>

              <div className="col-lg-4">
                <label className="form-label">Location</label>
                <input
                  className="form-control"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="Location"
                />
              </div>

              <div className="col-lg-4">
                <label className="form-label">Budget</label>
                <input
                  className="form-control"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  placeholder="Budget"
                />
              </div>

              <div className="col-lg-4">
                <label className="form-label">Timeline</label>
                <input
                  className="form-control"
                  value={formData.timeline}
                  onChange={(e) => handleChange('timeline', e.target.value)}
                  placeholder="Timeline"
                />
              </div>

              <div className="col-lg-12">
                <label className="form-label">Scope</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                  placeholder="Describe project scope"
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">Template Selection</label>
                <select
                  className="form-select"
                  value={formData.templateSelection}
                  onChange={(e) =>
                    handleChange('templateSelection', e.target.value)
                  }
                >
                  <option value="">Select template</option>
                  {templateOptions.map((template) => (
                    <option key={template} value={template}>
                      {template}
                    </option>
                  ))}
                </select>
              </div>
              {/* Form Actions - Synced with Invoice Page Style */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                  margin: '0 -24px -24px -24px', // Offset card body padding
                  borderRadius: '0 0 8px 8px',
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate('/sourcing/rfps')}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={!hasAnyInput || isSavingDraft || isGenerating}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: (!hasAnyInput || isSavingDraft || isGenerating) ? 'not-allowed' : 'pointer',
                    opacity: (!hasAnyInput || isSavingDraft || isGenerating) ? 0.7 : 1,
                  }}
                >
                  {isSavingDraft ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isGenerating || isSavingDraft || !hasAnyInput}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (isGenerating || isSavingDraft || !hasAnyInput) ? 'not-allowed' : 'pointer',
                    opacity: (isGenerating || isSavingDraft || !hasAnyInput) ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Save size={18} />
                  {isGenerating ? 'Generating...' : 'Generate RFP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AiRfpInput;
